/**
 * §B (Voice Profile) explicit-save buffer — pure state + ops, no React.
 *
 * Every §B edit is v2-homed (bb_voice_profile singleton fields, bb_chunks
 * rows); §B owns NO flat brand_bible columns, so nothing here ever routes
 * through the page's commitDraftFields seam. See useVoiceProfileSave.
 *
 * THE §B INVARIANT (architect handoff, Corrections 2026-08-17): deletes are
 * ID-KEYED and are NEVER synthesized from a list diff. A buffered delete
 * exists only because the operator clicked that specific row's remove
 * control; save() replays those exact ids. The one genuinely list-shaped
 * field, `attributes` on the singleton, is buffered as add/remove SET OPS and
 * resolved against the server list at save time (snapshot-then-diff) — never
 * as a captured replacement array, which would silently revert a concurrent
 * writer's addition.
 */
import type {
  BbEntry,
  BbEnvelope,
  ChunkPayload,
  VoiceProfilePayload,
} from '@/features/brand-bible-wizard/types';

/** Singleton fields §B buffers. `attributes` is deliberately NOT here — it is
 *  list-shaped and carries its own add/remove ops (see the invariant above). */
export type VoiceScalarField = Exclude<keyof VoiceProfilePayload, 'attributes'>;

export const TEMP_ID_PREFIX = 'bb-new:';

export const isTempId = (id: string): boolean => id.startsWith(TEMP_ID_PREFIX);

let tempSeq = 0;
/** Only ever called from an event handler, so StrictMode's double-invoked
 *  render can't burn ids or mint duplicates. */
export const nextTempId = (): string => `${TEMP_ID_PREFIX}${++tempSeq}`;

export interface NewChunkDraft {
  tempId: string;
  kind: NonNullable<ChunkPayload['kind']>;
  body: string;
  title?: string;
  channel?: string;
  why?: string;
}

export interface VoiceBuffer {
  /** Touched singleton scalars only — an untouched field is absent, so a save
   *  can never overwrite a value the operator never looked at (foreign-shape
   *  tolerance, §B L3 ruling). */
  fields: Partial<Record<VoiceScalarField, string>>;
  attrAdd: string[];
  attrDel: string[];
  rowNew: NewChunkDraft[];
  rowEdit: Record<string, Partial<ChunkPayload>>;
  rowDel: string[];
  /** Rows to verify AFTER their buffered patch lands — the golden→rejected
   *  reclassify is an edit (buffered) plus an operator act (immediate), and
   *  the act is only meaningful once the edit is on the server. */
  verifyAfterSave: string[];
}

export const emptyVoiceBuffer = (): VoiceBuffer => ({
  fields: {},
  attrAdd: [],
  attrDel: [],
  rowNew: [],
  rowEdit: {},
  rowDel: [],
  verifyAfterSave: [],
});

export const voiceBufferChangeCount = (b: VoiceBuffer): number =>
  Object.keys(b.fields).length +
  b.attrAdd.length +
  b.attrDel.length +
  b.rowNew.length +
  Object.keys(b.rowEdit).length +
  b.rowDel.length;

export const isVoiceBufferDirty = (b: VoiceBuffer): boolean => voiceBufferChangeCount(b) > 0;

/**
 * Buffer a scalar edit. Typing a field back to its stored value REMOVES it
 * from the buffer rather than queueing a no-op write — so "Save B · 2 changes"
 * only ever counts real divergence from the server.
 */
export function setScalar(
  b: VoiceBuffer,
  key: VoiceScalarField,
  value: string,
  stored: string | undefined | null,
): VoiceBuffer {
  const fields = { ...b.fields };
  if (value === (stored ?? '')) delete fields[key];
  else fields[key] = value;
  return { ...b, fields };
}

/** The value to render for a scalar: the buffered edit if the operator touched
 *  it, otherwise stored truth. */
export const scalarValue = (
  b: VoiceBuffer,
  key: VoiceScalarField,
  stored: string | undefined | null,
): string => b.fields[key] ?? stored ?? '';

export function addAttribute(b: VoiceBuffer, value: string): VoiceBuffer {
  const v = value.trim();
  if (!v) return b;
  // Re-adding something buffered for removal cancels the removal — it never
  // becomes a delete-then-add round trip.
  if (b.attrDel.includes(v)) return { ...b, attrDel: b.attrDel.filter((x) => x !== v) };
  if (b.attrAdd.includes(v)) return b;
  return { ...b, attrAdd: [...b.attrAdd, v] };
}

export function removeAttribute(b: VoiceBuffer, value: string): VoiceBuffer {
  if (b.attrAdd.includes(value)) return { ...b, attrAdd: b.attrAdd.filter((x) => x !== value) };
  if (b.attrDel.includes(value)) return b;
  return { ...b, attrDel: [...b.attrDel, value] };
}

/**
 * Snapshot-then-diff: apply the buffered set ops to whatever the server list
 * is RIGHT NOW. Called both for rendering (against the store's current list)
 * and, critically, at save time — so a value another writer added while the
 * buffer sat open survives the save instead of being reverted by a stale
 * captured array.
 */
export const resolveAttributes = (stored: readonly string[], b: VoiceBuffer): string[] => [
  ...stored.filter((a) => !b.attrDel.includes(a)),
  ...b.attrAdd.filter((a) => !stored.includes(a)),
];

export const hasAttributeOps = (b: VoiceBuffer): boolean =>
  b.attrAdd.length > 0 || b.attrDel.length > 0;

export function addRow(b: VoiceBuffer, draft: Omit<NewChunkDraft, 'tempId'>): VoiceBuffer {
  return { ...b, rowNew: [...b.rowNew, { ...draft, tempId: nextTempId() }] };
}

/**
 * Remove a row. A never-saved draft just leaves the buffer (no server row
 * exists). A persisted row's ID goes on the delete list — id-keyed, never
 * derived from a list comparison.
 */
export function removeRow(b: VoiceBuffer, id: string): VoiceBuffer {
  if (isTempId(id)) return { ...b, rowNew: b.rowNew.filter((r) => r.tempId !== id) };
  if (b.rowDel.includes(id)) return b;
  const rowEdit = { ...b.rowEdit };
  delete rowEdit[id];
  return {
    ...b,
    rowDel: [...b.rowDel, id],
    rowEdit,
    // A queued verify for a row being deleted is meaningless.
    verifyAfterSave: b.verifyAfterSave.filter((v) => v !== id),
  };
}

/** A draft's fields are narrower than ChunkPayload's (body is required, channel
 *  is never null), so a patch is applied key-by-key rather than spread. */
function applyDraftPatch(draft: NewChunkDraft, patch: Partial<ChunkPayload>): NewChunkDraft {
  return {
    ...draft,
    ...(patch.kind !== undefined ? { kind: patch.kind } : {}),
    ...(patch.title !== undefined ? { title: patch.title } : {}),
    ...(patch.body !== undefined ? { body: patch.body } : {}),
    ...(patch.channel != null ? { channel: patch.channel } : {}),
    ...(patch.why !== undefined ? { why: patch.why } : {}),
  };
}

export function editRow(b: VoiceBuffer, id: string, patch: Partial<ChunkPayload>): VoiceBuffer {
  if (isTempId(id)) {
    return {
      ...b,
      rowNew: b.rowNew.map((r) => (r.tempId === id ? applyDraftPatch(r, patch) : r)),
    };
  }
  return { ...b, rowEdit: { ...b.rowEdit, [id]: { ...b.rowEdit[id], ...patch } } };
}

export function queueVerifyAfterSave(b: VoiceBuffer, id: string): VoiceBuffer {
  if (isTempId(id) || b.verifyAfterSave.includes(id)) return b;
  return { ...b, verifyAfterSave: [...b.verifyAfterSave, id] };
}

/** One row as the panel renders it — a stored entry with any buffered patch
 *  applied, or a not-yet-saved draft. `isNew` gates every affordance that
 *  needs a server id (Verify, Reject). */
export interface VoiceRow {
  id: string;
  isNew: boolean;
  kind: NonNullable<ChunkPayload['kind']>;
  title: string | null;
  body: string | null;
  channel: string | null;
  why: string | null;
  public_mention: boolean | null;
  verified_by: string | null;
  verified_at: string | null;
  /** True when a buffered patch is sitting on a persisted row. */
  isEdited: boolean;
}

type StoredChunk = BbEntry<'chunks'>;

/**
 * The list the panel renders: stored rows minus buffered deletes, with
 * buffered patches applied, plus buffered new drafts of this kind. Purely a
 * VIEW — nothing here decides what gets written.
 */
export function mergeRows(
  stored: readonly StoredChunk[],
  b: VoiceBuffer,
  kind: NonNullable<ChunkPayload['kind']>,
): VoiceRow[] {
  const persisted = stored
    .filter((c) => !b.rowDel.includes(c.id))
    .map((c): VoiceRow => {
      const patch = b.rowEdit[c.id];
      const merged = { ...c, ...patch };
      return {
        id: c.id,
        isNew: false,
        kind: (merged.kind ?? kind) as NonNullable<ChunkPayload['kind']>,
        title: merged.title ?? null,
        body: merged.body ?? null,
        channel: merged.channel ?? null,
        why: merged.why ?? null,
        public_mention: merged.public_mention ?? null,
        verified_by: merged.verified_by ?? null,
        verified_at: merged.verified_at ?? null,
        isEdited: patch !== undefined,
      };
    })
    // A buffered `kind` change (reclassify) moves the row between lists in the
    // view immediately, exactly as it will on the server after Save.
    .filter((r) => r.kind === kind);

  const drafts = b.rowNew
    .filter((r) => r.kind === kind)
    .map(
      (r): VoiceRow => ({
        id: r.tempId,
        isNew: true,
        kind: r.kind,
        title: r.title ?? null,
        body: r.body,
        channel: r.channel ?? null,
        why: r.why ?? null,
        public_mention: null,
        verified_by: null,
        verified_at: null,
        isEdited: false,
      }),
    );

  return [...persisted, ...drafts];
}

/** Payload for a buffered draft, ready for `upsertEntry('chunks', …)`. */
export const draftPayload = (
  d: NewChunkDraft,
  section: BbEnvelope['section'],
): ChunkPayload => ({
  kind: d.kind,
  section,
  body: d.body,
  ...(d.title ? { title: d.title } : {}),
  ...(d.channel ? { channel: d.channel } : {}),
  ...(d.why ? { why: d.why } : {}),
});
